import { selMessage } from "@selector/shared";

import { createErrorHandler } from "@/app-utils/errors.ts";
import {
  type GroupList,
  type InputSetComparisonGroupInput,
  type InputSetComparisonInput,
  type SetComparison,
  type VariantAnalysisGroup,
} from "@/graphql.ts";
import {
  CREATE_GROUP_FROM_SET_COMPARISON_FAILURE,
  CREATE_GROUP_FROM_SET_COMPARISON_REQUEST,
  CREATE_GROUP_FROM_SET_COMPARISON_SUCCESS,
  LOAD_GROUPS_FAILURE,
  LOAD_GROUPS_REQUEST,
  LOAD_GROUPS_SUCCESS,
  LOAD_VARIANT_ANALYSIS_GROUP_FAILURE,
  LOAD_VARIANT_ANALYSIS_GROUP_REQUEST,
  LOAD_VARIANT_ANALYSIS_GROUP_SUCCESS,
  LOAD_VARIANT_GROUPS_FAILURE,
  LOAD_VARIANT_GROUPS_REQUEST,
  LOAD_VARIANT_GROUPS_SUCCESS,
  RESET_COMPARISON,
  SET_COMPARISON_FAILURE,
  SET_COMPARISON_REQUEST,
  SET_COMPARISON_SUCCESS,
  SHOW_MORE_THAN_TWO_SETS_WARNING,
} from "@/redux/action-types.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { notify } from "../../modules/notifications/notify.js";
import { createReducer } from "../redux-utils.js";

export interface IComparisonState {
  isLoading: boolean;
  isLoadingGroups: boolean;
  isLoadingVariantGroups: boolean;
  isLoadingVariantAnalysisGroup: boolean;
  type: string;
  setComparison: SetComparison;
  showedMoreThanTwoSetsWarning: boolean;
  groupId: number;
  groupData: GroupList;
  compatibleVariantGroupData: GroupList;
  variantAnalysisGroup: VariantAnalysisGroup;
}

export const initialState: IComparisonState = {
  isLoading: false,
  isLoadingGroups: false,
  isLoadingVariantGroups: false,
  isLoadingVariantAnalysisGroup: false,
  type: null,
  setComparison: null,
  showedMoreThanTwoSetsWarning: false,
  groupId: null,
  groupData: null,
  compatibleVariantGroupData: null,
  variantAnalysisGroup: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [SET_COMPARISON_REQUEST]: (state: IComparisonState, payload: InputSetComparisonInput) => {
    return { ...state, isLoading: true, setComparison: null, type: payload.type };
  },
  [SET_COMPARISON_SUCCESS]: (state: IComparisonState, payload: SetComparison) => {
    return { ...state, isLoading: false, setComparison: payload };
  },
  [SET_COMPARISON_FAILURE]: (state: IComparisonState) => {
    return { ...state, isLoading: false, setComparison: undefined };
  },
  [SHOW_MORE_THAN_TWO_SETS_WARNING]: (state: IComparisonState) => {
    return { ...state, showedMoreThanTwoSetsWarning: true };
  },
  [RESET_COMPARISON]: (state: IComparisonState) => {
    return { ...state, ...initialState };
  },
  [CREATE_GROUP_FROM_SET_COMPARISON_REQUEST]: (state: IComparisonState) => {
    return { ...state, groupId: null };
  },
  [CREATE_GROUP_FROM_SET_COMPARISON_SUCCESS]: (state: IComparisonState, payload: number) => {
    return { ...state, groupId: payload };
  },
  [CREATE_GROUP_FROM_SET_COMPARISON_FAILURE]: (state: IComparisonState) => {
    return { ...state, groupId: null };
  },
  [LOAD_GROUPS_REQUEST]: (state: IComparisonState) => {
    return { ...state, isLoadingGroups: true };
  },
  [LOAD_GROUPS_SUCCESS]: (state: IComparisonState, payload: GroupList) => {
    return { ...state, isLoadingGroups: false, groupData: payload };
  },
  [LOAD_GROUPS_FAILURE]: (state: IComparisonState) => {
    return { ...state, isLoadingGroups: false };
  },

  [LOAD_VARIANT_ANALYSIS_GROUP_REQUEST]: (state: IComparisonState) => {
    return { ...state, isLoadingVariantAnalysisGroup: true };
  },
  [LOAD_VARIANT_ANALYSIS_GROUP_SUCCESS]: (state: IComparisonState, payload: VariantAnalysisGroup) => {
    return { ...state, isLoadingVariantAnalysisGroup: false, variantAnalysisGroup: payload };
  },
  [LOAD_VARIANT_ANALYSIS_GROUP_FAILURE]: (state: IComparisonState) => {
    return { ...state, isLoadingVariantAnalysisGroup: false };
  },
  [LOAD_VARIANT_GROUPS_REQUEST]: (state: IComparisonState) => {
    return { ...state, isLoadingVariantGroups: true };
  },
  [LOAD_VARIANT_GROUPS_SUCCESS]: (state: IComparisonState, payload: GroupList) => {
    return { ...state, isLoadingVariantGroups: false, groupData: payload };
  },
  [LOAD_VARIANT_GROUPS_FAILURE]: (state: IComparisonState) => {
    return { ...state, isLoadingVariantGroups: false };
  },
});

function performSetComparisonRequest(payload: InputSetComparisonInput) {
  return {
    type: SET_COMPARISON_REQUEST,
    payload,
  };
}

function performSetComparisonSuccess(payload: SetComparison) {
  return {
    type: SET_COMPARISON_SUCCESS,
    payload,
  };
}

function showedMoreThanTwoSetsWarningAction() {
  return {
    type: SHOW_MORE_THAN_TWO_SETS_WARNING,
  };
}

function reset() {
  return {
    type: RESET_COMPARISON,
  };
}

export function resetComparisonState() {
  return (dispatch): void => {
    dispatch(reset());
  };
}

function createGroupFromSetComparisonRequest(payload: InputSetComparisonGroupInput) {
  return {
    type: CREATE_GROUP_FROM_SET_COMPARISON_REQUEST,
    payload,
  };
}

function createGroupFromSetComparisonSuccess(payload: number) {
  return {
    type: CREATE_GROUP_FROM_SET_COMPARISON_SUCCESS,
    payload,
  };
}

function loadGroupsRequest(type: string) {
  return {
    type: LOAD_GROUPS_REQUEST,
    payload: type,
  };
}

function loadGroupsSuccess(payload: GroupList) {
  return {
    type: LOAD_GROUPS_SUCCESS,
    payload,
  };
}

export function loadGroups(type: string) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<SetComparison> => {
    dispatch(loadGroupsRequest(type));
    return webApiClient
      .loadGroups(type)
      .then((response: GroupList) => {
        dispatch(loadGroupsSuccess(response));
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_GROUPS_FAILURE,
          alertTitle: "Loading of Groups Failed",
          alertMessage: `Error while fetching group data`,
        }),
      );
  };
}

export function loadCompatibleVariantGroups(analysisGroupId: number) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({
      type: LOAD_VARIANT_GROUPS_REQUEST,
    });
    return webApiClient
      .loadCompatibleVariantGroups(analysisGroupId)
      .then((response: GroupList) => {
        dispatch({
          type: LOAD_VARIANT_GROUPS_SUCCESS,
          payload: response,
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_VARIANT_GROUPS_FAILURE,
          alertTitle: "Loading of Compatible Variant Groups Failed",
          alertMessage: "Error while fetching compatible variant group data",
        }),
      );
  };
}

export function loadVariantComparisonData(type: string, initialVariantGroupId: number, initialVariantId: number) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<SetComparison> => {
    dispatch({ type: LOAD_VARIANT_ANALYSIS_GROUP_REQUEST });
    return webApiClient
      .loadVariantAnalysisGroup(type, initialVariantGroupId, initialVariantId)
      .then((response: VariantAnalysisGroup) => {
        dispatch({
          type: LOAD_VARIANT_ANALYSIS_GROUP_SUCCESS,
          payload: response,
        });
        dispatch(loadCompatibleVariantGroups(response.id));
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_VARIANT_ANALYSIS_GROUP_FAILURE,
          alertTitle: "Loading of Genome Analysis Group Failed",
          alertMessage: `Error while fetching genome analysis group data`,
        }),
      );
  };
}

export function performSetComparison(payload: InputSetComparisonInput) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<SetComparison> => {
    dispatch(performSetComparisonRequest(payload));
    return webApiClient
      .performSetComparison(payload)
      .then((response: SetComparison) => {
        dispatch(performSetComparisonSuccess(response));
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SET_COMPARISON_FAILURE,
          alertTitle: "Set Comparison Failed",
          alertMessage: `Error while fetching set comparison data`,
        }),
      );
  };
}

export function createGroupFromSetComparison(payload: InputSetComparisonGroupInput) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<SetComparison> => {
    dispatch(createGroupFromSetComparisonRequest(payload));
    return webApiClient
      .createGroupFromSetComparison(payload)
      .then((response: number) => {
        dispatch(createGroupFromSetComparisonSuccess(response));
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: CREATE_GROUP_FROM_SET_COMPARISON_FAILURE,
          alertTitle: "Creating Group From Set Comparison Failed",
          alertMessage: `Error while fetching group data`,
        }),
      );
  };
}

export function createWarning(selectedItems: number) {
  return (dispatch, getState, _webApiClient: WebApiClient): void => {
    if (!getState().comparison.showedMoreThanTwoSetsWarning && selectedItems > 2) {
      dispatch(showedMoreThanTwoSetsWarningAction());
      notify.warning(
        {
          title: selMessage({ id: "vennDiagram.limitExceeded.warningTitle" }),
          children: selMessage({ id: "vennDiagram.limitExceeded.warningMessage" }),
        },
        { autoClose: 3000 },
      );
    }
  };
}
